// As text, not a stylesheet: injected only once the theme is known, along with the
// --loader-accent it depends on.
import progressStyles from './progress.css?raw';
import { PROGRESS_CEILING, nextProgress } from './progress.ts';
import {
    type BootTheme,
    DARK_SCHEME_QUERY,
    TAILWIND_DARK_CLASS,
    resolveBootTheme,
} from './theme.ts';

export function startBootLoader(theme: BootTheme = resolveTheme()): void {
    // Never taken back: useColorMode sets the same class on mount, and until then this is what
    // stops the admin stylesheet painting a light body over the dark backdrop.
    if (theme.tailwindDark) {
        document.body.classList.add(TAILWIND_DARK_CLASS);
    }

    const container = document.querySelector<HTMLElement>('#root .loader');
    if (container) {
        startProgressBar(container, theme);
    }
}

function resolveTheme(): BootTheme {
    try {
        return resolveBootTheme({
            pathname: window.location.pathname,
            readStorage: (key) => {
                // Storage access throws in Safari private mode; a light page beats a broken boot.
                try {
                    return localStorage.getItem(key);
                } catch {
                    return null;
                }
            },
            prefersDark:
                typeof window.matchMedia === 'function' &&
                window.matchMedia(DARK_SCHEME_QUERY).matches,
        });
    } catch {
        return { tailwindDark: false };
    }
}

function startProgressBar(loaderElement: HTMLElement, theme: BootTheme) {
    const style = document.createElement('style');
    const track = document.createElement('div');
    const bar = document.createElement('div');
    let observer: PerformanceObserver | undefined;

    const teardown = () => {
        observer?.disconnect();
        style.remove();
        track.remove();
    };

    try {
        style.textContent = progressStyles + bodyRule(theme);
        document.head.append(style);

        track.className = 'loader-progress';
        // Decorative — .loader carries the progressbar role.
        track.setAttribute('aria-hidden', 'true');
        bar.className = 'loader-progress__bar';
        track.append(bar);

        const expected = collectExpectedRequests();
        // Read now rather than awaited from the observer: buffered entries arrive a tick later,
        // and on a warm cache the app can mount before that.
        const landed = collectLandedRequests(expected);

        observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
                if (expected.has(entry.name)) {
                    landed.add(entry.name);
                }
            }
        });
        observer.observe({ type: 'resource', buffered: true });

        let previousTime: number | undefined;
        let settled = false;
        let shown = nextProgress({
            shown: 0,
            landed: landed.size,
            expected: expected.size,
            deltaSeconds: 0,
        });

        // Set before insertion, so a warm cache never flashes an empty groove.
        bar.style.setProperty('--loader-progress', String(shown));
        loaderElement.append(track);

        const frame = (now: number) => {
            // React replaces the contents of #root wholesale, taking the bar with it.
            if (!track.isConnected) {
                teardown();
                return;
            }

            const next = nextProgress({
                shown,
                landed: landed.size,
                expected: expected.size,
                deltaSeconds: previousTime === undefined ? 0 : (now - previousTime) / 1000,
            });
            previousTime = now;

            if (next !== shown) {
                shown = next;
                bar.style.setProperty('--loader-progress', String(shown));
            }
            if (!settled && shown >= PROGRESS_CEILING) {
                settled = true;
                observer?.disconnect();
            }

            requestAnimationFrame(frame);
        };

        requestAnimationFrame(frame);
    } catch {
        // Runs before Sentry init, so a throw here is invisible in production. Fall back to
        // the plain spinner rather than leave a dead bar.
        teardown();
    }
}

function bodyRule(theme: BootTheme): string {
    const declarations = [
        theme.color && `--loader-color: ${theme.color};`,
        theme.background && `background: ${theme.background};`,
    ].filter(Boolean);

    return declarations.length ? `\nbody {\n    ${declarations.join('\n    ')}\n}` : '';
}

function collectLandedRequests(expected: Set<string>): Set<string> {
    const landed = new Set<string>();

    for (const entry of performance.getEntriesByType('resource')) {
        if (expected.has(entry.name)) {
            landed.add(entry.name);
        }
    }

    return landed;
}

// Vite emits one <link rel="modulepreload"> per static dependency of the app entry, so the
// document itself says how many requests must land before React can boot. Dev has none — its
// two module scripts resolve at once and mean nothing — and an empty set falls back to creep.
function collectExpectedRequests(): Set<string> {
    const urls = new Set<string>();
    const preloads = document.querySelectorAll('link[rel="modulepreload"][href]');
    if (preloads.length === 0) return urls;

    const nodes = [...preloads, ...document.querySelectorAll('script[type="module"][src]')];

    for (const node of nodes) {
        const path = node.getAttribute('href') ?? node.getAttribute('src');
        if (!path) continue;

        const url = new URL(path, document.baseURI).href;
        if (url !== import.meta.url) {
            urls.add(url);
        }
    }

    return urls;
}
